import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiStepperItem, NUMBER_FROM_STEP, StepKey } from '../../model/client-onboarding/onboarding-wizard-stepper';
import { BehaviorSubject, catchError, distinctUntilChanged, finalize, map, Observable, of, shareReplay, startWith, tap } from 'rxjs';
import { ServiceGroup } from '../../model/client-onboarding/services-settings';
import { BackendAckResponse, CredentialEntry, CredentialPayload, CredentialItem, TitanSource } from '../../model/client-onboarding/credentials';

@Injectable({ providedIn: 'root' })
export class OnboardingWizardService {
  constructor(private http: HttpClient) { }

  private readonly _steps$ = new BehaviorSubject<ApiStepperItem[]>([]);
  readonly steps$ = this._steps$.asObservable().pipe(shareReplay(1));
  stepSaved$ = new BehaviorSubject<StepKey>('clientDataReview');
  get stepsSnapshot(): ApiStepperItem[] { return this._steps$.value; }

  ackState: { isAcked: boolean; haveCrm: boolean } | null = null;
  pendingCredentials: CredentialItem[] = [];

  private loading = false;
  private readonly createClientbaseUrl = 'onboarding-client';
  private readonly onboardingWizardbaseUrl = 'onboarding-wizard';

  // stepper
  public getStepper(onboardingId: number): Observable<ApiStepperItem[]> {
    const url = `${this.createClientbaseUrl}/steppers/${onboardingId}`;
    return this.http.get<{ result: ApiStepperItem[] }>(url).pipe(
      map(res => res.result ?? [])
    );
  }

  isBlocked$(currentStep: number, onboardingId: number) {
    return this.refresh(onboardingId).pipe(
      startWith(this.stepsSnapshot),
      map((items) => {
        const stepNum =
          typeof currentStep === 'number'
            ? currentStep
            : /^\d+$/.test(currentStep as string)
              ? Number(currentStep)
              : NUMBER_FROM_STEP[currentStep as keyof typeof NUMBER_FROM_STEP];
        if (!items || items.length === 0) return null as unknown as boolean;
        const it = items.find(x => +x.step === stepNum);
        const raw = (it?.status ?? '').toString().trim().toLowerCase().replace(/[\s_-]/g, '');
        const open = raw === 'inprogress' || raw === 'completed';
        return !open;
      }),
      distinctUntilChanged(),
      catchError(() => of(false)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  refresh(onboardingId: number): Observable<ApiStepperItem[]> {
    if (this.loading) return of(this.stepsSnapshot);

    this.loading = true;
    const url = `${this.createClientbaseUrl}/steppers/${onboardingId}`;
    return this.http.get<{ result: ApiStepperItem[] }>(url).pipe(
      map(res => res?.result ?? []),
      tap(items => {
        this._steps$.next(items);
      }),
      catchError(err => {
        console.error('[refresh] erro', err);
        return of(this.stepsSnapshot);
      }),
      finalize(() => (this.loading = false))
    );
  }

  saveStepData(payload: any) {
    return this.http.post(`${this.onboardingWizardbaseUrl}`, payload);
  }

  saveACH(payload: any) {
    return this.http.post(`${this.onboardingWizardbaseUrl}/ach`, payload);
  }

  getACH(onboardingId: number) {
    return this.http.get<any>(`${this.onboardingWizardbaseUrl}/ach/${onboardingId}`);
  }

  // mids
  getStepMidData(onboardingId: number) {
    const url = `${this.onboardingWizardbaseUrl}/mids/${onboardingId}`;
    return this.http.get<any>(url).pipe(map(res => res.result));
  }

  deleteMidData(midId: number) {
    return this.http.delete(`${this.onboardingWizardbaseUrl}/mids/${midId}`);
  }

  // services
  getServicesMids(onboardingId: number) {
    const url = `${this.onboardingWizardbaseUrl}/services/${onboardingId}/mids`;
    return this.http.get<{ message: string; result: { services: ServiceGroup[] } }>(url)
      .pipe(map(res => res.result?.services ?? []));
  }

  getHiredServices(onboardingId: number) {
    const url = `${this.onboardingWizardbaseUrl}/services/${onboardingId}`;
    return this.http.get<any>(url)
      .pipe(map(res => res.result));
  }

  //creds
  getCredsMids(onboardingId: number) {
    const url = `${this.onboardingWizardbaseUrl}/acknowledgements/${onboardingId}`;
    return this.http.get<BackendAckResponse>(url);
  }

  getCredentials(onboardingId: number): Observable<CredentialEntry[]> {
    const url = `${this.onboardingWizardbaseUrl}/credentials/${onboardingId}`;
    return this.http.get<{ message: string; result: CredentialEntry[] }>(url).pipe(
      map(res => res?.result ?? []),
      catchError(() => of([]))
    );
  }

  saveCredential(payload: CredentialPayload) {
    return this.http.post(`${this.onboardingWizardbaseUrl}/credentials`, payload);
  }

  getTitanSources(): Observable<TitanSource[]> {
    const url = `${this.onboardingWizardbaseUrl}/titan-sources`;
    return this.http.get<{ message: string; result: TitanSource[] }>(url).pipe(
      map(res => res?.result ?? []),
      catchError(() => of([]))
    );
  }

  //review
  getReviewData(onboardingId: number) {
    const url = `${this.onboardingWizardbaseUrl}/client-data/review/${onboardingId}`;
    return this.http.get<{ message: string; result: any }>(url)
      .pipe(map(res => res.result?.reviewed ?? []));
  }

  //fd-review
  getFdReview(onboardingId: number) {
    const url = `${this.onboardingWizardbaseUrl}/fd-review/${onboardingId}`;
    return this.http.get<{ message: string; result: any }>(url);

  }

  //change status
  changeClientStatusToActive(onboardingId: number) {
    const body = '';
    return this.http.post(`${this.onboardingWizardbaseUrl}/client/change-status/${onboardingId}`, body)
  }


  getClientServices(onboardingId: number) {
    const url = `${this.onboardingWizardbaseUrl}/services/${onboardingId}`;
    return this.http.get<any>(url);
  }

  //save mids draft 
  saveDraft(payload) {
    return this.http.post(`${this.onboardingWizardbaseUrl}/autosave`, payload)
  }

  // ── Merchant Contract ──

  private readonly contractBaseUrl = 'merchant-contract';

  /** GET /merchant-contract/contract-details */
  getContractDetails(): Observable<any> {
    return this.http.get<any>(`${this.contractBaseUrl}/contract-details`);
  }

  /** GET /merchant-contract/show-contract/{id} */
  showContract(onboardingId: number): Observable<any> {
    return this.http.get<any>(`${this.contractBaseUrl}/show-contract/${onboardingId}`).pipe(
      catchError(() => of({ result: false }))
    );
  }

  /** GET /merchant-contract/show-contract/woocommerce */
  showContractWooCommerce(): Observable<any> {
    return this.http.get<any>(`${this.contractBaseUrl}/show-contract/woocommerce`).pipe(
      catchError(() => of({ result: false }))
    );
  }

  /** GET /merchant-contract/show-contract-nexio/{id} */
  showContractNexio(onboardingId: number): Observable<any> {
    return this.http.get<any>(`${this.contractBaseUrl}/show-contract-nexio/${onboardingId}`).pipe(
      catchError(() => of({ showContract: false }))
    );
  }

  /** POST /merchant-contract/contract-acceptance/{id} */
  acceptContract(onboardingId: number, payload: any): Observable<any> {
    return this.http.post(`${this.contractBaseUrl}/contract-acceptance/${onboardingId}`, payload);
  }

  /** GET /merchant-contract/contract-details/woocommerce */
  getContractDetailsWooCommerce(): Observable<any> {
    return this.http.get<any>(`${this.contractBaseUrl}/contract-details/woocommerce`);
  }

  /** POST /merchant-contract/contract-acceptance/woocommerce/{id} */
  acceptContractWooCommerce(onboardingId: number): Observable<any> {
    return this.http.post(`${this.contractBaseUrl}/contract-acceptance/woocommerce/${onboardingId}`, {});
  }
}
